/**
 * THE VOICE CONTROLLER: every voice decision on /oral, and nothing else.
 *
 * The page feeds it socket events and the student's Start and End taps, and renders its
 * state; the controller decides what is spoken, when the microphone records, and when a
 * spoken answer is sent. Its outside world is injected (an audio player, a voice-service
 * client, a microphone, a clock, and the page's answer sender), which is what lets the
 * tests drive it with fakes and assert what it tells the world to do.
 *
 * THE STUDENT ENDS THE ANSWER (#18). Two taps, Start and End, never a press-and-hold. An
 * open mic ended by a pause detector cut the owner off at a thinking pause on the first
 * phone test, and the examiner jumped in mid-answer: a pause can't tell "thinking" from
 * "done". So nothing ends an answer but End, or the cap, which sends it.
 *
 * Voice is an extra on top of the text, never a dependency of the exam: the lines are on
 * screen whatever happens here, and any failure ends in chat with a notice, never in a
 * page waiting on audio.
 */

import { encodeWav, WAV_RATE } from "./wav";

export interface AudioPlayer {
  /** Called from the Begin click: browsers refuse audio that no gesture started. */
  unlock(): void;
  /** Resolves when the audio has finished playing, or once `stop` cuts it off. */
  play(audio: ArrayBuffer): Promise<void>;
  stop(): void;
}

export interface VoiceClient {
  /** One sentence of text in, its audio out. Rejects on any refusal. */
  speak(text: string): Promise<ArrayBuffer>;
  /** A 16 kHz mono WAV in, its words out ("" for nothing intelligible). Rejects on any
   *  refusal, and on a service that can't be reached. */
  transcribe(wav: ArrayBuffer): Promise<string>;
}

/**
 * IOS'S AUDIO SESSION (#19). With no microphone open, iOS Safari treats the page's Web
 * Audio as ambient sound, which the ring/silent switch mutes completely: no error, nothing
 * the page can detect. Before #18 the mic opened at Begin and its play-and-record session
 * hid this. So: playback from Begin, play-and-record only from Start until the recording
 * ends, then playback again, or iOS can send the next reply to the earpiece.
 */
export type SessionType = "playback" | "play-and-record";

export interface AudioSession {
  set(type: SessionType): void;
}

const NO_SESSION: AudioSession = { set() {} };

/** Held only from a Start tap to its End: the student is never on an open mic. */
export interface Microphone {
  /** From the Start tap. Resolves once it is recording; rejects if the microphone is
   *  refused or won't start. `failed` is for it stopping working after that. */
  start(failed: () => void): Promise<void>;
  /** Stop and let go of the microphone: the whole recording as 16 kHz mono WAV, or null
   *  if nothing was recorded. */
  stop(): ArrayBuffer | null;
  /** Let go of the microphone and throw away the recording. Safe at any time. */
  close(): void;
}

/** One voice turn, the End tap to the examiner's first audio, in ms. */
export type Latency = {
  total: number;
  /** The End tap to the transcript back. */
  transcription: number;
  /** Answer sent to the examiner's first new line. */
  examiner: number;
  /** That line to its first sentence playing. */
  speech: number;
};

export type Mode = "voice" | "chat";

export type VoiceState = {
  /** Voice by default; chat on the student's switch or on any voice failure, and back
   *  to voice on the switch. */
  mode: Mode;
  /** Audio is being fetched or played. */
  speaking: boolean;
  /** Start was tapped and the microphone is on its way (the permission prompt, the
   *  first time). End now cancels: nothing has been recorded. */
  opening: boolean;
  /** The microphone is taking an answer, until End or the cap. */
  recording: boolean;
  /** When the recording began, on the controller's clock, for the page's timer. */
  since: number | null;
  /** CAP_WARNING_MS or less of the answer cap is left. */
  warning: boolean;
  /** A Start tap would begin an answer: voice, the student's turn, nothing under way. */
  canStart: boolean;
  /** An answer has ended and is with the voice service. */
  transcribing: boolean;
  /** The words just sent, until they come back as the student's line. */
  heard: string | null;
  /** From an answer sent until the examiner's next line. */
  thinking: boolean;
  notice: string | null;
  /** The last voice turn's timing, or null before one has completed. */
  latency: Latency | null;
};

export const QUIET: VoiceState = {
  mode: "voice",
  speaking: false,
  opening: false,
  recording: false,
  since: null,
  warning: false,
  canStart: false,
  transcribing: false,
  heard: null,
  thinking: false,
  notice: null,
  latency: null,
};

/** Between a Start tap and its End: the tap or the spacebar now ends the answer. */
export const answering = (s: VoiceState) => s.opening || s.recording;

export const VOICE_FAILED =
  "Voice stopped working, so the exam carries on in chat: type your answers below. Nothing is lost.";
export const MIC_FAILED =
  "The microphone isn’t available, so the exam carries on in chat: type your answers below.";
export const DIDNT_CATCH = "I didn’t catch that. Tap Start and say it again?";

//: THE ANSWER CAP, in seconds: the voice service's VOICE_MAX_AUDIO_S default, and never
//: more, or every answer that reached it would be refused 413 and drop the student to
//: chat (site.test.ts checks the two agree). The page sends the answer when it is reached.
export const MAX_ANSWER_S = 180;

//: The warning comes with this much left, long enough to finish a sentence.
export const CAP_WARNING_MS = 15_000;

//: `?cap=N` (seconds) lowers the cap, for the voice suite's cap check, which must not take
//: three minutes. It can only lower it: a higher cap is the 413 above.
export const CAP_PARAM = "cap";

export function answerCapMs(search: string): number {
  const asked = Number(new URLSearchParams(search).get(CAP_PARAM));
  return asked > 0 && asked < MAX_ANSWER_S ? asked * 1000 : MAX_ANSWER_S * 1000;
}

/**
 * THE SPACEBAR starts and ends the answer on a computer, but not where a space means
 * something else: in a text field it types a space, and on a focused button the browser
 * clicks the button itself. A tapped Start keeps the focus, so handling the space there
 * too would start and end in one press.
 */
export function spaceToggles(e: {
  key: string;
  repeat?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  target: { tagName?: string; isContentEditable?: boolean } | null;
}): boolean {
  if (e.key !== " " || e.repeat || e.ctrlKey || e.metaKey || e.altKey) return false;
  const t = e.target;
  if (!t) return true;
  if (t.isContentEditable) return false;
  return !["INPUT", "TEXTAREA", "SELECT", "BUTTON"].includes((t.tagName ?? "").toUpperCase());
}

//: Comfortably under the voice service's VOICE_MAX_TEXT_CHARS default of 500. A sentence
//: past it would be refused with a 413 and take the voice down for the rest of the exam.
const MAX_SENTENCE_CHARS = 400;

//: Whitespace after a terminal mark, allowing a closing quote or bracket. Decimals and
//: regulation numbers ("91.155", "3.5 miles") have no space after the point, so they hold.
const SENTENCE_END = /(?<=[.!?…]["'”’)\]]*)\s+|\n+/;

/**
 * SENTENCE BY SENTENCE: the first sentence's audio is ready long before a whole reply's
 * would be, which saved 1-3 s per multi-sentence reply in the predecessor projects.
 */
export function splitSentences(text: string): string[] {
  const out: string[] = [];
  for (const sentence of text.split(SENTENCE_END)) {
    let chunk = "";
    for (const word of sentence.split(/\s+/).filter(Boolean)) {
      if (chunk && chunk.length + 1 + word.length > MAX_SENTENCE_CHARS) {
        out.push(chunk);
        chunk = word;
      } else {
        chunk = chunk ? `${chunk} ${word}` : word;
      }
    }
    if (chunk) out.push(chunk);
  }
  return out;
}

export type VoiceController = ReturnType<typeof createVoiceController>;

export function createVoiceController({
  player,
  voice,
  mic,
  session = NO_SESSION,
  send,
  onChange,
  now = () => performance.now(),
  after = (ms, fn) => {
    const t = setTimeout(fn, ms);
    return () => clearTimeout(t);
  },
  maxAnswerMs = MAX_ANSWER_S * 1000,
}: {
  player: AudioPlayer;
  voice: VoiceClient;
  mic: Microphone;
  /** iOS's audio session; elsewhere the default, which does nothing. */
  session?: AudioSession;
  /** The page's own answer path, the one a typed answer takes. */
  send: (text: string) => void;
  onChange?: (state: VoiceState) => void;
  now?: () => number;
  /** Run `fn` in `ms`; returns its cancel. */
  after?: (ms: number, fn: () => void) => () => void;
  maxAnswerMs?: number;
}) {
  let state = QUIET;
  //: THE HIGHEST LOG POSITION SEEN, spoken or only shown. The socket replays the whole
  //: log on every connect (ADR 0001), so arrival means nothing; position is the only
  //: thing that says a line is new.
  let heard = -1;
  type Queued = { text: string; audio?: Promise<ArrayBuffer> };
  let queue: Queued[] = [];
  let playing = false;
  //: Bumped by stop and by failure, so a playback loop still awaiting audio from before
  //: drops out instead of playing into a queue that is no longer its own.
  let run = 0;
  //: Bumped by every Start and every close, so a microphone that opens after the student
  //: ended, switched away or left never records, and its late failure blames nothing.
  let micRun = 0;
  //: The cap's warning and its send, cancelled whenever the recording ends any other way.
  let capTimers: (() => void)[] = [];
  //: THE STUDENT'S TURN: from `ready` to an answer, typed or spoken, or the oral's end.
  //: An answer sent outside it is refused `busy` by the exam server and lost.
  let turn = false;
  //: Bumped whenever the turn moves on, so a transcript or a failure from an answer that
  //: is no longer wanted is dropped rather than sent or blamed on voice.
  let answer = 0;
  //: TIMESTAMPS ONLY, taken at events that happen anyway, so measuring never delays the
  //: voice path. Cleared whenever the turn being timed can no longer finish as one.
  let timing: { ended: number; sent?: number; line?: number } | null = null;

  const set = (patch: Partial<VoiceState>) => {
    const next = { ...state, ...patch };
    next.canStart =
      next.mode === "voice" && turn && !next.opening && !next.recording && !next.transcribing;
    state = next;
    onChange?.(state);
  };

  //: ONE SENTENCE AHEAD, requested while the current one plays, so the gap between
  //: sentences is playback-bound. Never more: a long reply fetched all at once would
  //: spend the voice service's per-minute rate limit in a burst.
  const fetchHead = () => {
    const head = queue[0];
    if (head && !head.audio) {
      head.audio = voice.speak(head.text);
      head.audio.catch(() => {}); // awaited where it is played; not unhandled meanwhile
    }
  };

  const silence = () => {
    run++;
    queue = [];
    playing = false;
    timing = null;
    player.stop();
  };

  const NOT_RECORDING = { opening: false, recording: false, since: null, warning: false };

  //: THE ONE WAY THE MICROPHONE IS LET GO: End and the cap (after taking the recording),
  //: the switch, a failure, the oral's end, leaving the page, and an End before it opened.
  //: So it is also the one place playback comes back, and no ending can miss it.
  const closeMic = () => {
    micRun++;
    capTimers.forEach((cancel) => cancel());
    capTimers = [];
    mic.close();
    session.set("playback");
  };

  //: INTO CHAT, by the switch or by a failure: the same way, so the switch can always
  //: bring voice back. A recording is thrown away and an answer still being transcribed
  //: is dropped (`answer`), or it would arrive while the student types the same thing.
  const toChat = (notice: string | null) => {
    answer++;
    silence();
    closeMic();
    set({ ...NOT_RECORDING, mode: "chat", speaking: false, transcribing: false, notice });
  };

  const fail = (notice: string) => {
    if (state.mode === "chat") return;
    toChat(notice);
  };

  const turnOver = () => {
    turn = false;
    answer++;
    if (answering(state)) closeMic();
    set({ ...NOT_RECORDING, transcribing: false });
  };

  const startAnswer = () => {
    if (!state.canStart) return;
    //: BARGE-IN, by the tap. The student starting over the examiner wins; the rest of the
    //: line is on screen. Only at the student's turn: the examiner's turn has ended and
    //: its last line is just still being played.
    if (state.speaking) {
      silence();
      set({ speaking: false });
    }
    const mine = ++micRun;
    set({ opening: true, notice: null });
    //: Before the microphone is asked for, inside the tap, and never earlier: before
    //: Start the student is not on a microphone, not even as far as iOS can tell.
    session.set("play-and-record");
    mic
      .start(() => {
        if (mine === micRun) fail(MIC_FAILED);
      })
      .then(
        () => {
          if (mine !== micRun) return;
          capTimers = [
            after(maxAnswerMs - CAP_WARNING_MS, () => set({ warning: true })),
            //: AT THE CAP THE ANSWER IS SENT, not lost: the student said it.
            after(maxAnswerMs, endAnswer),
          ];
          set({ opening: false, recording: true, since: now(), warning: false });
        },
        () => {
          if (mine === micRun) fail(MIC_FAILED);
        },
      );
  };

  function endAnswer() {
    if (state.opening) {
      //: Nothing recorded yet: an upload of nothing would be refused and read as voice
      //: failing. Back to Start, as if never tapped.
      closeMic();
      set(NOT_RECORDING);
      return;
    }
    if (!state.recording) return;
    const wav = mic.stop();
    closeMic();
    set(NOT_RECORDING);
    if (!wav) {
      set({ notice: DIDNT_CATCH });
      return;
    }
    set({ transcribing: true });
    timing = { ended: now() };
    const mine = answer;
    voice.transcribe(wav).then(
      (text) => {
        if (mine !== answer) return;
        set({ transcribing: false });
        const words = text.trim();
        if (!words) {
          //: A cough or an "um" is not an answer (v3 sent "uh..." and the examiner read
          //: it as the student stalling). Back to Start, never straight to recording.
          set({ notice: DIDNT_CATCH });
          return;
        }
        turnOver();
        if (timing) timing.sent = now();
        send(words);
        set({ heard: words, thinking: true, notice: null });
      },
      () => {
        if (mine === answer) fail(VOICE_FAILED);
      },
    );
  }

  const playQueue = async () => {
    if (playing) return;
    playing = true;
    const mine = run;
    set({ speaking: true });
    try {
      while (queue.length && mine === run) {
        fetchHead();
        const audio = await queue.shift()!.audio!;
        if (mine !== run) return;
        fetchHead();
        if (timing?.line !== undefined) {
          const { ended, sent = ended, line } = timing;
          const t = now();
          timing = null;
          set({
            latency: {
              total: t - ended,
              transcription: sent - ended,
              examiner: line - sent,
              speech: t - line,
            },
          });
        }
        await player.play(audio);
      }
    } catch {
      if (mine === run) fail(VOICE_FAILED);
    } finally {
      if (mine === run) {
        playing = false;
        set({ speaking: false });
      }
    }
  };

  //: Begin, and back into voice: both from a click, both back to playback.
  const unlock = () => {
    session.set("playback");
    player.unlock();
  };

  return {
    state: () => state,

    unlock,

    /** The Start tap, from the click itself: the microphone's permission prompt wants a
     *  gesture behind it. */
    startAnswer,

    /** The End tap. */
    endAnswer,

    /** The answer button and the spacebar: Start, or End once started. */
    toggleAnswer() {
      if (answering(state)) endAnswer();
      else startAnswer();
    },

    /** The student's switch. From a click, so the player can be unlocked again. */
    switchTo(mode: Mode) {
      if (mode === state.mode) return;
      if (mode === "chat") return toChat(null);
      //: Lines shown while in chat stay unspoken: `heard` moved on with them.
      set({ mode: "voice", notice: null });
      unlock();
    },

    /** A `said` message, with its position in the connection's stream of them. */
    said(position: number, who: "examiner" | "applicant", text: string) {
      if (position <= heard) return;
      heard = position;
      if (who !== "examiner") {
        if (state.heard !== null) set({ heard: null });
        return;
      }
      if (state.thinking) set({ thinking: false });
      if (timing?.sent !== undefined && timing.line === undefined) timing.line = now();
      if (state.mode === "chat") return;
      queue.push(...splitSentences(text).map((t) => ({ text: t })));
      fetchHead(); // a line arriving mid-sentence is fetched while that sentence plays
      void playQueue();
    },

    /** The exam server will take an answer now: Start is offered. */
    ready() {
      turn = true;
      set({});
    },

    /** A typed answer went out. */
    answered() {
      timing = null;
      turnOver();
      set({ thinking: true });
    },

    ended() {
      timing = null;
      turnOver();
      set({ thinking: false });
    },

    /** Leaving the page: cut the audio, drop whatever was queued, let go of the mic. */
    stop() {
      turnOver();
      silence();
      closeMic();
      set({ speaking: false });
    },
  };
}

// ------------------------------------------------- the browser's side of the seams

/**
 * Web Audio rather than an <audio> element: a context resumed inside the Begin click
 * stays unlocked for the whole exam, where an element has to be unlocked per element.
 */
export function webAudioPlayer(
  makeContext: () => AudioContext = () => new AudioContext(),
): AudioPlayer {
  let ctx: AudioContext | null = null;
  let source: AudioBufferSourceNode | null = null;
  //: Bumped by every stop. A decode still under way at a barge-in, the switch or a
  //: failure would otherwise start its sentence over the student's answer (#19).
  let generation = 0;
  return {
    unlock() {
      try {
        ctx ??= makeContext();
        void ctx.resume();
        //: iOS Safari unlocks only once a sound has actually STARTED inside the gesture.
        const blip = ctx.createBufferSource();
        blip.buffer = ctx.createBuffer(1, 1, 22050);
        blip.connect(ctx.destination);
        blip.start();
      } catch {
        ctx = null; // no Web Audio: play() refuses, and that is the notice
      }
    },
    async play(audio) {
      //: A context the browser kept suspended would accept the audio and never finish
      //: it, leaving the queue stuck with no notice. Refuse loudly instead.
      if (!ctx || ctx.state !== "running") throw new Error("audio is blocked");
      const mine = generation;
      const out = ctx;
      const buffer = await out.decodeAudioData(audio);
      if (mine !== generation) return;
      await new Promise<void>((resolve) => {
        const s = out.createBufferSource();
        s.buffer = buffer;
        s.connect(out.destination);
        s.onended = () => {
          if (source === s) source = null;
          resolve();
        };
        source = s;
        s.start();
      });
    },
    stop() {
      generation++;
      source?.stop();
      source = null;
    },
  };
}

//: `navigator.audioSession` is Safari's (iOS 17+) and not in every DOM lib: typed here.
type WithAudioSession = { audioSession?: { type: string } };

const audioSessionOf = (nav: object) => (nav as WithAudioSession).audioSession;

/** iOS's audio session where Safari has the API; elsewhere nothing, and never a throw. */
export function browserAudioSession(nav: object): AudioSession {
  const api = audioSessionOf(nav);
  return {
    set(type) {
      if (!api) return;
      try {
        api.type = type;
      } catch {} // a type the browser refuses leaves it as it was: no worse than no API
    },
  };
}

export function hasAudioSession(nav: object): boolean {
  return Boolean(audioSessionOf(nav));
}

export const SILENT_MODE_HINT = "Can’t hear the examiner? Turn off silent mode.";

/**
 * Only on iOS without the audio session API, where the silent switch mutes the examiner
 * and the page can neither fix it nor tell. iPadOS reports a Mac's user agent, so a "Mac"
 * with a touch screen counts as iOS.
 */
export function silentModeHint({
  userAgent,
  maxTouchPoints,
  hasAudioSession,
}: {
  userAgent: string;
  maxTouchPoints: number;
  hasAudioSession: boolean;
}): boolean {
  const ios = /iPhone|iPad|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1);
  return ios && !hasAudioSession;
}

//: THE CAPTURE WORKLET, inline: it hands each block of samples to the page. Inline rather
//: than a file under public/, which would be one more thing that can be missing at the
//: first Start. It writes nothing to its output, so what it is connected to stays silent.
const CAPTURE = `registerProcessor("capture", class extends AudioWorkletProcessor {
  process(inputs) {
    const samples = inputs[0] && inputs[0][0];
    if (samples) {
      const copy = samples.slice(0);
      this.port.postMessage(copy, [copy.buffer]);
    }
    return true;
  }
});`;

/**
 * The microphone in the browser: from the Start tap to End, every sample, kept whole and
 * encoded once at End. No speech detector: the student ends the answer, not a pause (#18).
 *
 * At most `maxSeconds` is kept, so an End that lands a moment after the cap still sends
 * an answer the voice service will take.
 */
export function captureMicrophone(maxSeconds: number): Microphone {
  //: ONE SESSION PER START. A start still opening when it was closed must shut itself on
  //: arrival without touching the session that replaced it.
  type Session = {
    stream: MediaStream | null;
    ctx: AudioContext | null;
    chunks: Float32Array[];
    kept: number;
    closed: boolean;
    onFail: () => void;
  };
  let current: Session | null = null;

  const shut = (s: Session) => {
    s.closed = true;
    s.onFail = () => {};
    s.stream?.getTracks().forEach((t) => t.stop());
    void s.ctx?.close().catch(() => {});
    s.ctx = null;
  };

  return {
    start(failed) {
      if (current) shut(current);
      const s: Session = {
        stream: null,
        ctx: null,
        chunks: [],
        kept: 0,
        closed: false,
        onFail: failed,
      };
      current = s;
      //: BOTH INSIDE THE TAP, before the first await: the permission prompt and a
      //: running audio context each want a gesture behind them, Safari's most of all.
      let asked: Promise<MediaStream>;
      try {
        asked = navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        s.ctx = new AudioContext();
        void s.ctx.resume();
      } catch (e) {
        //: No mediaDevices off a secure origin, no AudioContext in an old browser: the
        //: same refusal as a denied prompt, so the same notice.
        return Promise.reject(e);
      }
      const ctx = s.ctx;
      return (async () => {
        try {
          const opened = await asked;
          s.stream = opened;
          if (s.closed) return;
          //: A headset unplugged mid-answer ends the track; say so, don't record nothing.
          opened.getAudioTracks().forEach((t) => (t.onended = () => s.onFail()));
          const url = URL.createObjectURL(new Blob([CAPTURE], { type: "text/javascript" }));
          try {
            await ctx.audioWorklet.addModule(url);
          } finally {
            URL.revokeObjectURL(url);
          }
          if (s.closed) return;
          const node = new AudioWorkletNode(ctx, "capture");
          const limit = Math.floor(maxSeconds * ctx.sampleRate);
          node.port.onmessage = (ev: MessageEvent<Float32Array>) => {
            const room = limit - s.kept;
            if (room <= 0) return;
            const block = ev.data.length > room ? ev.data.subarray(0, room) : ev.data;
            s.chunks.push(block);
            s.kept += block.length;
          };
          ctx.createMediaStreamSource(opened).connect(node);
          //: Connected through to the speakers only so that the browser runs it; it
          //: writes silence.
          node.connect(ctx.destination);
        } finally {
          //: Closed while opening, or failed after the stream arrived: either way the
          //: microphone light goes off.
          if (s.closed) shut(s);
        }
      })();
    },
    stop() {
      const s = current;
      if (!s) return null;
      current = null;
      const rate = s.ctx?.sampleRate ?? WAV_RATE;
      shut(s);
      if (!s.kept) return null;
      const samples = new Float32Array(s.kept);
      let at = 0;
      for (const block of s.chunks) {
        samples.set(block, at);
        at += block.length;
      }
      return encodeWav(samples, rate);
    },
    close() {
      const s = current;
      if (!s) return;
      current = null;
      shut(s);
    },
  };
}

export function voiceService(base: string): VoiceClient {
  return {
    async transcribe(wav) {
      const r = await fetch(`${base}/transcribe`, {
        method: "POST",
        headers: { "Content-Type": "audio/wav" },
        body: wav,
      });
      if (!r.ok) throw new Error(`the voice service said ${r.status}`);
      const { text } = await r.json();
      if (typeof text !== "string") throw new Error("the voice service sent no text");
      return text;
    },
    async speak(text) {
      const r = await fetch(`${base}/speak`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!r.ok) throw new Error(`the voice service said ${r.status}`);
      return r.arrayBuffer();
    },
  };
}

//: The readout is for the operator checking the latency target, not for students, so it
//: shows only on /oral?latency.
export const LATENCY_PARAM = "latency";

export function showsLatency(search: string): boolean {
  return new URLSearchParams(search).has(LATENCY_PARAM);
}

const secs = (ms: number) => `${(ms / 1000).toFixed(1)} s`;

/** Voice is the target's measure (≤2.5 s p50, ≤4 s p95): the total less the examiner. */
export function latencyReadout(l: Latency): string {
  return (
    `voice ${secs(l.transcription + l.speech)} ` +
    `(transcription ${secs(l.transcription)} + speech ${secs(l.speech)}) · ` +
    `examiner ${secs(l.examiner)} · total ${secs(l.total)}`
  );
}
